import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context';

import { zindex } from 'src/ui/zindex';

import { AppState } from 'src/reducers';

import { ButtonIcon, ButtonSize, ButtonType } from '@prisjakt/ui/button';
import {
  Dropdown,
  DropdownContent,
  DropdownToggle,
} from '@prisjakt/ui/dropdown';
import {
  IconCompare,
  IconHeartOutlined,
  IconHeartFilled,
  IconMenuKebab,
  IconNotificationFilled,
  IconNotificationOutlined,
} from '@prisjakt/ui/icon';
import { Direction } from '@prisjakt/ui';

import { ListItem } from 'src/components/dropdown-new';
import {
  addItemToMyProductsRequest,
  addListItemRequest,
  selectList,
} from 'src/areas/sidebar/actions/lists'; // Should not depend on other areas

import { ListModal } from './list-modal';
import { useOnAction } from '../../utils';
import LoginPromoModal from 'src/components/modal/login-promo';

import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';

import { UserProductAlert } from 'src/areas/sidebar/types';
import { ProductPageQuery } from 'src/types';
import { Me } from '../../types';

import { ListContainer } from './styles';

type Props = {
  className?: string;
  product: NonNullable<ProductPageQuery['product']>;
};

export const ActionButtonsMobile = (props: Props) => {
  const { className, product } = props;
  const { linkTo, t } = useAppContext();

  const dispatch = useDispatch();

  const [addedToList, setAddedToListState] = useState<boolean>(false);
  const [listOpen, setListOpen] = useState(false);
  const [showLoginPromo, setLoginPromoState] = useState(false);

  const me: Me = useSelector((state: AppState) => state.areas.account.me);
  const userProductAlerts = useSelector(
    (state: AppState) => state.areas.sidebar.alerts.products
  );
  const deletedLists = useSelector(
    (state: AppState) => state.areas.sidebar.deletedLists
  );
  const selectedList = useSelector(
    (state: AppState) => state.areas.sidebar.selectedList
  );

  const isAuthenticated = useIsAuthenticated();
  const userAlert = userProductAlerts.find(
    (alert: UserProductAlert) => alert.productId === product.id
  );
  const alertId = userAlert && userAlert.alertId;
  const isBookmarked = alertId && isAuthenticated;
  const onAction = useOnAction();

  const latestLists =
    me?.lists?.nodes
      ?.filter(list => deletedLists[list.id] !== true)
      .filter(list => !list.inventory)
      .sort(a => {
        if (a.id === selectedList) {
          return 1;
        }

        return 0;
      }) ?? [];

  const handleListClick = () => setListOpen(state => !state);

  const handleAlertClick = () => onAction('alert-create');

  const addToList = (listId?: number) => {
    dispatch(selectList(listId));
    dispatch(addListItemRequest(product.id, product.name, false));
    onAction('list-add');
    setAddedToListState(true);
  };

  const addToMyProducts = () => {
    dispatch(addItemToMyProductsRequest(product.id, product.name, false));
    onAction('list-add');
    setAddedToListState(true);
  };

  const toggleLoginPromo = () => setLoginPromoState(!showLoginPromo);

  return (
    <div className={className}>
      {listOpen && (
        <ListModal
          addToList={addToList}
          addToMyProducts={addToMyProducts}
          isOpen={listOpen}
          closeModal={() => setListOpen(false)}
          lists={latestLists}
        />
      )}

      {showLoginPromo && (
        <LoginPromoModal
          isOpen={showLoginPromo}
          onRequestClose={toggleLoginPromo}
        />
      )}

      <Dropdown
        style={{ height: 'fit-content' }}
        zIndex={zindex.menu.sticky.background.isSticky + 1}
      >
        <DropdownToggle
          onlyIcon
          type={ButtonType.TONAL}
          size={ButtonSize.MEDIUM}
        >
          <ButtonIcon>
            <IconMenuKebab />
          </ButtonIcon>
        </DropdownToggle>
        <DropdownContent alignment={Direction.Right} width={200}>
          <ListContainer style={{ listStyle: 'none', margin: '0' }}>
            {/* Alerts */}
            {product.prices?.nodes?.length ? (
              <li>
                <ListItem
                  ariaLabel={t('product', 'alert.create-button')}
                  onClick={handleAlertClick}
                >
                  {isBookmarked ? (
                    <IconNotificationFilled />
                  ) : (
                    <IconNotificationOutlined />
                  )}
                  {t('product', 'alert.create-button')}
                </ListItem>
              </li>
            ) : null}

            {/* Lists */}
            <li>
              <ListItem
                ariaLabel={t('common', 'button.save')}
                onClick={isAuthenticated ? handleListClick : toggleLoginPromo}
              >
                {addedToList ? <IconHeartFilled /> : <IconHeartOutlined />}
                {addedToList
                  ? t('common', 'button.saved')
                  : t('common', 'button.save')}
              </ListItem>
            </li>

            {/* Compare */}
            <li>
              <ListItem
                ariaLabel={t('product', 'product-compare.compare')}
                rel="nofollow"
                to={linkTo('productCompare', {
                  category: product.category,
                  products: [product],
                })}
              >
                <IconCompare />
                {t('product', 'product-compare.compare')}
              </ListItem>
            </li>
          </ListContainer>
        </DropdownContent>
      </Dropdown>
    </div>
  );
};
